import React, { useState, useEffect } from 'react';
import { useSidebar } from '../../contexts/SidebarContext';
import Sidebar from '../../components/ui/Sidebar';
import TopBar from '../../components/ui/TopBar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import supplierService from '../../services/supplierService';
import { userRoleService } from '../../services/userRoleService';
import SupplierCard from './components/SupplierCard';
import RoleAssignmentModal from './components/RoleAssignmentModal';
import CapacityManagementModal from './components/CapacityManagementModal';


const SupplierManagementDashboard = () => {
  const { isCollapsed, toggleSidebar } = useSidebar();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showCapacityModal, setShowCapacityModal] = useState(false);
  const [roles, setRoles] = useState([]);

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
    { label: 'Gestion Fournisseurs', path: '/supplier-management-dashboard', icon: 'Users' }
  ];

  useEffect(() => {
    fetchSuppliers();
    fetchRoles();
  }, []);

  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await supplierService?.getAllSuppliers();
      setSuppliers(data);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
      setError('Erreur lors du chargement des fournisseurs');
      setSuppliers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const data = await userRoleService?.getAllRoles();
      setRoles(data);
    } catch (err) {
      console.error('Error fetching roles:', err);
    }
  };

  const handleAssignRole = (supplier) => {
    setSelectedSupplier(supplier);
    setShowRoleModal(true);
  };

  const handleManageCapacity = (supplier) => {
    setSelectedSupplier(supplier);
    setShowCapacityModal(true);
  };

  const handleRoleAssigned = async () => {
    setShowRoleModal(false);
    await fetchSuppliers();
  };

  const handleCapacityUpdated = async () => {
    setShowCapacityModal(false);
    await fetchSuppliers();
  };

  const filteredSuppliers = suppliers?.filter(supplier => {
    const matchesSearch = 
      supplier?.fullName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      supplier?.companyName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      supplier?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    
    const matchesFilter = 
      filterActive === 'all' ||
      (filterActive === 'active' && supplier?.isActive) ||
      (filterActive === 'inactive' && !supplier?.isActive);
    
    return matchesSearch && matchesFilter;
  });

  const activeSuppliers = suppliers?.filter(s => s?.isActive)?.length || 0;
  const totalCapacity = suppliers?.reduce((sum, s) => sum + (s?.capacity?.maxCapacity || 0), 0);
  const avgQualityScore = suppliers?.length > 0
    ? (suppliers?.reduce((sum, s) => sum + (s?.capacity?.qualityScore || 0), 0) / suppliers?.length)?.toFixed(1)
    : 0;

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <Sidebar isCollapsed={isCollapsed} onToggle={toggleSidebar} />
      <div className="flex-1 flex flex-col overflow-hidden">
      <TopBar sidebarCollapsed={isCollapsed} onSidebarToggle={toggleSidebar} />
      <main className={`main-content ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
          <div className="container mx-auto px-4 py-6 max-w-7xl">
            <Breadcrumb items={breadcrumbItems} />

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Gestion des Fournisseurs</h1>
                <p className="text-sm text-gray-600 mt-1">
                  Gestion complète des fournisseurs de production et rôles doubles
                </p>
              </div>
            </div>

            {/* Statistics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Fournisseurs</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{suppliers?.length}</p>
                  </div>
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Icon name="Users" size={24} className="text-blue-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Fournisseurs Actifs</p>
                    <p className="text-2xl font-bold text-green-600 mt-1">{activeSuppliers}</p>
                  </div>
                  <div className="bg-green-100 p-3 rounded-lg">
                    <Icon name="CheckCircle" size={24} className="text-green-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Capacité Totale</p>
                    <p className="text-2xl font-bold text-purple-600 mt-1">{totalCapacity}</p>
                  </div>
                  <div className="bg-purple-100 p-3 rounded-lg">
                    <Icon name="TrendingUp" size={24} className="text-purple-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Score Qualité Moyen</p>
                    <p className="text-2xl font-bold text-orange-600 mt-1">{avgQualityScore}/5</p>
                  </div>
                  <div className="bg-orange-100 p-3 rounded-lg">
                    <Icon name="Star" size={24} className="text-orange-600" />
                  </div>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Icon name="Search" size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Rechercher par nom, entreprise ou email..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e?.target?.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant={filterActive === 'all' ? 'primary' : 'outline'}
                    onClick={() => setFilterActive('all')}
                  >
                    Tous
                  </Button>
                  <Button
                    variant={filterActive === 'active' ? 'primary' : 'outline'}
                    onClick={() => setFilterActive('active')}
                  >
                    Actifs
                  </Button>
                  <Button
                    variant={filterActive === 'inactive' ? 'primary' : 'outline'}
                    onClick={() => setFilterActive('inactive')}
                  >
                    Inactifs
                  </Button>
                </div>
              </div>
            </div>

            {/* Suppliers Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : error ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                <Icon name="AlertCircle" size={24} className="text-red-600 mx-auto mb-2" />
                <p className="text-red-600">{error}</p>
              </div>
            ) : filteredSuppliers?.length === 0 ? (
              <div className="bg-white rounded-lg shadow p-8 text-center">
                <Icon name="Users" size={48} className="text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">Aucun fournisseur trouvé</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredSuppliers?.map(supplier => (
                  <SupplierCard
                    key={supplier?.id}
                    supplier={supplier}
                    onAssignRole={() => handleAssignRole(supplier)}
                    onManageCapacity={() => handleManageCapacity(supplier)}
                  />
                ))}
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Modals */}
      {showRoleModal && selectedSupplier && (
        <RoleAssignmentModal
          supplier={selectedSupplier}
          roles={roles}
          onClose={() => setShowRoleModal(false)}
          onSuccess={handleRoleAssigned}
        />
      )}

      {showCapacityModal && selectedSupplier && (
        <CapacityManagementModal
          supplier={selectedSupplier}
          onClose={() => setShowCapacityModal(false)}
          onSuccess={handleCapacityUpdated}
        />
      )}
    </div>
  );
};

export default SupplierManagementDashboard;